import { KatDropdown, KatInput } from '@amzn/katal-react';
import Datetime from 'react-datetime';
import moment from 'moment';
import 'react-datetime/css/react-datetime.css';
import { CMSMetadata, Slot } from '@amzn/fire-tv-campaign-automation-service';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { useCampaignMetadata } from 'src/hooks/useCampaignMetadata';
import styles from './CampaignMetadataInputSection.module.css';
import {
  CAMPAIGN_METADATA_CONFIG,
  getSlotOptionsForPageId,
} from 'src/configs/campaignMetadataConfig';
import { useEffect, useState } from 'react';

interface DateTimeInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  'data-testid'?: string;
}

export const CampaignMetadataInputSection = () => {
  const { metadata, updateMetadata } = useCampaignMetadata();
  /**
   * State to store dropdown options for metadata fields.
   * Maps metadata keys to arrays of name-value option pairs.
   */
  const [optionsMap, updateOptionsMap] = useState<
    Partial<
      Record<
        keyof CMSMetadata,
        {
          name: string;
          value: string;
        }[]
      >
    >
  >({
    slotName: getSlotOptionsForPageId(metadata.pageId),
  });

  /**
   * Updates slot options when page ID changes and selects the first slot by default.
   * @dependency metadata.pageId - Re-runs when the page ID changes
   */
  useEffect(() => {
    const slots = getSlotOptionsForPageId(metadata.pageId);
    updateOptionsMap((prev) => ({
      ...prev,
      slotName: slots,
    }));

    // Set slotName to first option in the list if available
    if (slots && slots.length > 0) {
      updateMetadata({ slotName: slots[0].value as Slot });
    }
  }, [metadata.pageId]);

  const handleDateChange = (
    date: string | moment.Moment,
    metadataItem: keyof CMSMetadata,
  ): void => {
    if (date && typeof date !== 'string' && date.isValid()) {
      updateMetadata({ [metadataItem]: date.toDate() });
    }
  };

  const yesterday = moment().subtract(1, 'day').startOf('minute');
  const isValidStartDate = (current: moment.Moment): boolean => {
    // Compare at minute precision to allow selecting current time
    const currentMinute = current.clone().startOf('minute');
    return currentMinute.isSameOrAfter(yesterday);
  };

  const isValidEndDate = (current: moment.Moment): boolean => {
    const startDate = metadata.campaignStartDate;
    if (!startDate) return false;

    // Compare at minute precision
    const currentMinute = current.clone().startOf('minute');
    const startMinute = moment(startDate).startOf('minute');
    return currentMinute.isAfter(startMinute);
  };

  const renderInput = (props: DateTimeInputProps, testId: string) => (
    <input {...props} data-testid={testId} />
  );

  const handleInputChange = (key: keyof CMSMetadata, event: KatInput.ChangeEvent) => {
    updateMetadata({ [key]: event.currentTarget.value });
  };

  const handleDropdownChange = (key: keyof CMSMetadata, event: KatDropdown.ChangeEvent) => {
    updateMetadata({ [key]: event.detail.value });
  };

  const renderMetadataItem = (metadataItem: keyof CMSMetadata) => {
    const config = CAMPAIGN_METADATA_CONFIG[metadataItem];
    if (!config) return null;

    const key = metadataItem as keyof CMSMetadata;

    switch (config.type) {
      case 'text':
        return (
          <KatInput
            type="text"
            label={config.label}
            value={metadata[key]?.toString()}
            onChange={(e) => handleInputChange(key, e)}
            placeholder={config.placeholder}
            data-testid={config.dataTestId}
            required={config.required}
            min={config.minLength?.toString()}
            maxlength={config.maxLength?.toString()}
            inputmode="text"
          />
        );
      case 'date-time':
        return (
          <>
            <label className={styles.dateTimeLabel}>{config.label}</label>
            <Datetime
              value={metadata[key]}
              onChange={(date) => handleDateChange(date, key)}
              isValidDate={metadataItem === 'campaignStartDate' ? isValidStartDate : isValidEndDate}
              closeOnSelect={false}
              renderInput={(props) => renderInput(props, config.dataTestId)}
              inputProps={{
                placeholder: config.placeholder,
                className: styles.dateTimeInput,
              }}
              className={styles.dateTimePicker}
              dateFormat="YYYY-MM-DD"
              timeFormat="HH:mm"
              initialViewMode="days"
              initialViewDate={metadata[key] || moment()}
            />
          </>
        );
      case 'drop-down':
        return (
          <StyledDropdown
            label={config.label}
            value={metadata[key]?.toString()}
            onChange={(e) => handleDropdownChange(metadataItem, e)}
            options={optionsMap[metadataItem]?.length ? optionsMap[metadataItem] : config.options} // Uses dynamic options from optionsMap if available, otherwise falls back to static config options
            placeholder={config.placeholder}
            data-testid={config.dataTestId}
          />
        );
      default:
        return null;
    }
  };

  // Group metadata items by their rowGroup
  const groupedMetadata = Object.entries(CAMPAIGN_METADATA_CONFIG).reduce<
    Record<string, Array<keyof CMSMetadata>>
  >((acc, [key, config]) => {
    const group = config.rowGroup || 'default';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(key as keyof CMSMetadata);
    return acc;
  }, {});

  return (
    <div className={styles.container} data-testid="campaign-metadata-section">
      {Object.entries(groupedMetadata).map(([group, items]) => (
        <div key={group} className={styles.row}>
          {items.map((item) => {
            const config = CAMPAIGN_METADATA_CONFIG[item];
            if (!config) return null;

            return (
              <div
                key={item}
                className={`${styles.inputGroup} ${
                  config.width === 'half' ? styles.halfWidth : styles.fullWidth
                }`}
              >
                {renderMetadataItem(item)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
